/* eslint-disable no-useless-escape */
import showdown from "showdown";

const converter = new showdown.Converter({
  tables: true, // Enable GitHub-style tables
  simplifiedAutoLink: true, // Auto-detect links
  strikethrough: true, // Enable ~~strikethrough~~
  tasklists: true, // Enable GitHub task lists [ ] and [x]
  smoothLivePreview: true, // Smooth preview mode
  openLinksInNewWindow: true, // Open links in a new tab
  emoji: true, // Enable emoji support :smile:
  parseImgDimensions: true, // Enable image dimensions like ![image](url =100x200)
  metadata: true, // Allow metadata at the start of the document
  ghCodeBlocks: true, // Enable GitHub-style code blocks
  ghCompatibleHeaderId: true, // GitHub-compatible headers
  disableForced4SpacesIndentedSublists: true, // Prevent forced 4-space indents
  literalMidWordUnderscores: true, // Keep underscores inside words
  literalMidWordAsterisks: true, // Keep asterisks inside words
  encodeEmails: true, // Encode email addresses
  smartIndentationFix: true, // Fix indentation issues
  underline: true, // Enable underline with __text__
  completeHTMLDocument: false, // Generate full HTML document
  backslashEscapesHTMLTags: true, // Escape HTML tags with backslash
  // disableForced4SpacesIndentedSublists: true, // Fix sublist issues
  requireSpaceBeforeHeadingText: false, // Allow no space before headers
  splitAdjacentBlockquotes: false, // Prevent splitting blockquotes
});

export const isValidArray = (arr: TObject) =>
  arr && Array.isArray(arr) && arr.length;

export const isValidId = (id: TObject) => id && Number(id) > 0;

/**
 * Updates the `isExpended` property of the question at the specified index.
 * Sets the `isExpended` property to the provided value for the specified index,
 * and sets it to `false` for all other questions.
 *
 * @param {IFaqs[]} questions - The array of question objects.
 * @param {number} index - The index of the question to update.
 * @param {boolean} value - The boolean value to set for the `isExpended` property.
 * @returns {IFaqs[]} The updated array of question objects with the `isExpended` property updated.
 */
export const updateisExpended = (
  questions: IFaqs[],
  index: number,
  value: boolean
): IFaqs[] => {
  return questions.map((question, i) => ({
    ...question,
    isExpended: i === index ? value : false,
  }));
};

export const getLast4Digits = (cardNumber: string): string => {
  return cardNumber?.slice(-4);
};

/**
 * Adds a PDF ID to each item in the chat history array.
 * @param chatHistory - The original chat history array.
 * @param pdfId - The PDF ID to be added.
 * @returns A new chat history array with the PDF ID added to each item.
 */
export const addPdfIdToChatHistory = (
  chatHistory: any[],
  pdfId: string
): any[] => {
  return chatHistory.map((chat) => ({
    ...chat,
    pdf_id: pdfId,
  }));
};

/**
 * Filters chat history records by a specific PDF ID.
 * @param chatHistory - The chat history array.
 * @param pdfId - The PDF ID to filter by.
 * @returns A new array containing only the records with the specified PDF ID.
 */
export const filterChatHistoryByPdfId = (
  chatHistory: any[],
  pdfId: string
): any[] => {
  // Step 1: Filter the chat history based on the pdfId
  const filteredHistory = chatHistory.filter((chat) => chat?.pdf_id === pdfId);

  // Step 2: Remove pdf_id from each element in the filtered array
  const result = filteredHistory.map(({ pdf_id, ...rest }) => rest);

  return result;
};

export const carouselSliderProps = {
  superLargeDesktop: {
    // the naming can be any, depends on you.
    breakpoint: { max: 4000, min: 3000 },
    items: 5,
  },
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 3,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 2,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 3,
  },
};

export function textToMarkdown(inputText: any) {
  if (!inputText) return "";
  let markdown = typeof inputText === "string" ? inputText : inputText?.answer;

  // Basic Markdown formatting
  markdown = markdown
    ?.replace(/^"|"$/g, "")
    ?.replace(/\\n/g, "\n")
    ?.replace(/\n/g, "  \n");

  markdown = markdown?.replace(/\r\n/g, "\n");
  markdown = markdown?.replace(/\n{3,}/g, "\n\n");

  markdown = markdown?.replace(/^### (.*)/gm, "### $1");
  markdown = markdown?.replace(/^## (.*)/gm, "## $1");
  markdown = markdown?.replace(/^# (.*)/gm, "# $1");

  markdown = markdown?.replace(/(\*\*|__)(.*?)\1/g, "**$2**");
  // Only convert underscore emphasis with word boundaries (to preserve underscores in filenames like file_name.docx)
  markdown = markdown?.replace(/(\*)(.*?)\1/g, "*$2*");
  markdown = markdown?.replace(/\b_(.*?)_\b/g, "*$1*");

  // Replace unordered list dashes with bullet points (•)
  markdown = markdown?.replace(/^[-\*\+] (.*)/gm, "• &nbsp; $1");

  markdown = markdown?.replace(/^(\d+)\. (.*)/gm, "$1. $2");

  markdown = markdown?.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/g,
    "[$1]($2)"
  );

  markdown = markdown?.replace(
    /!\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/g,
    "![$1]($2)"
  );

  markdown = markdown?.replace(/^> (.*)/gm, "> $1");

  markdown = markdown?.replace(/`(.*?)`/g, "`$1`");

  markdown = markdown?.replace(/```([\s\S]*?)```/g, "```$1```");

  markdown = markdown?.replace(/^(---|\*\*\*|___)$/gm, "---");

  markdown = markdown?.replace(/~~(.*?)~~/g, "~~$1~~");

  // Improved table detection and conversion
  markdown = markdown?.replace(/^(.*\|.*)$/gm, (match: any) => {
    const rows = match?.split("\n");
    if (rows?.length < 2) return match; // Not a table if less than 2 rows

    let table = "";
    for (let i = 0; i < rows?.length; i++) {
      if (i === 1) {
        // Add the separator line for the table header
        const columns = rows?.[i]?.split("|")?.length - 1;
        table += "|" + "---|".repeat(columns) + "\n";
      }
      table += rows?.[i] + "\n";
    }
    return table;
  });

  markdown = markdown?.replace(/\\([*_~`#])/g, "\\$1");

  markdown = markdown?.replace(/\n/g, "\n");

  return converter.makeHtml(markdown);
}
